import Vue from 'vue';
import FileSlingerListUploader from '@/components/s3_files/FileSlingerListUploader.vue';

// RENDERER -- just shows links to files
// -------------------------------------------------
const AgGridFileRenderer = Vue.extend({
    template: `
        <div class="grid-file-renderer w-100" v-b-hover="handleHover">
            <div v-if="hasFiles" key="hasFiles" class="d-flex flex-row align-items-center justify-content-between position-relative">
                <div class="flex-grow-1 d-flex gap-1 flex-column py-1" style="line-height: 1.1;">
                    <div v-for="file in localFileObjects" :key="file.id">
                        <a :href="file.download_url" target="_blank">{{ file.filename }}</a>
                    </div>
                </div>
                <transition name="slide-in-from-right">
                <div v-if="isEditable && showEditButton" class="grid-file-edit-button position-absolute d-flex align-items-center justify-content-center" style="top: 0; right: 0; height: 100%; width: 50%; max-width: 60px;">
                    <b-button variant="zone-dark" class="text-decoration-none" @click="toggleEditing">
                        <!-- <b-icon icon="pencil"></b-icon> -->
                        <div class="font-size-70 special-text"> Edit</div>
                    </b-button>
                </div>
                </transition>
            </div>
            <div v-else key="noFiles" class="w-100 d-flex flex-row align-items-center justify-content-between">
                <b-button v-if="isEditable" size="sm" variant="zone-secondary" class="text-muted text-decoration-none" @click="toggleEditing">
                    <b-icon icon="plus"></b-icon> Add Files
                </b-button>
                <div v-else class="text-muted">No Files</div>
            </div>
        </div>
    `,  // NOTE: this relies on some CSS in the parent grid to make it look right -- see FormulateAgGrid.vue
    data() {
        return {
            localFileObjects: [],
            showEditButton: false, // controlled by handleHover
        }
    },
    computed: {
        parentComponent() {
            return this.params.context.componentParent;
        },
        liveGridCol() {
            return this.parentComponent.liveGridColsByField[this.params.colDef.field];
        },
        hasFiles() {
            return this.localFileObjects && Array.isArray(this.localFileObjects) && this.localFileObjects.length > 0;
        },
        isEditable() {
            return this.params.colDef.editable;  // might be in read-only mode...
        },
    },
    created() {
        this.setValueFromParams();
    },
    mounted() {},
    methods: {
        refreshCell() {
            // called by grid when things change, we need to get data from params.value again
            this.setValueFromParams();
        },
        setValueFromParams() {
            // called on create and on refresh, anytime params.value changes
            if (this.params.value && Array.isArray(this.params.value)) {
                this.localFileObjects = this.params.value.map(f => ({...f}));  // shallow copy, always an array
            } else {
                this.localFileObjects = [];
            }
        },
        getIsCurrentlyEditing() {
            const editingCells = this.params.api.getEditingCells();  // returns array of CellPosition objects: [{rowIndex: 0, column: Column, rowPinned: string}, ...]
            const thisColId = this.params.column.getId();
            const thisRowIndex = this.params.node.rowIndex;
            return editingCells.some((cell) => {
                return cell.column.getId() === thisColId && cell.rowIndex === thisRowIndex;
            });
        },
        toggleEditing() {
            if (this.getIsCurrentlyEditing()) {
                this.stopEditing();
            } else {
                this.startEditing(false);  // no saving of changes when just toggling?
            }
        },
        stopEditing(cancel = false) {
            // its possible for the user to click again on the button (depending on alignment of the editor component), so sometimes we need to allow stopping
            // pass cancel=true to cancel the edit (not save changes)
            this.params.api.stopEditing(cancel);
        },
        startEditing() {
            // called locally from our button, to start editing this cell
            this.params.api.startEditingCell({
                rowIndex: this.params.node.rowIndex,
                colKey: this.params.column.getId(),
            });
        },
        handleHover(isHovering) {
            this.showEditButton = isHovering;
        },
    },
});


// EDITOR -- shows file uploader component (in popup)
// -------------------------------------------------
const AgGridFileEditor = Vue.extend({
    components: {
        FileSlingerListUploader,
    },
    template: `
        <div ref="fileEditorOuter" tabindex="0" class="" style="width: 500px;">
            <FileSlingerListUploader
                ref="fileUploader"
                :category="fileCategory"
                :parentId="parentId"
                :existingFiles="initialFileObjects"
                inputHeight="120px"
                inputWrapperClass="w-100"
                listWrapperClass="w-100"
                wrapperClass=""
                :smallList="smallList"
                :hideLimits="hideLimits"
                :imageOnly="imageOnly"
                :accept="accept"
                :maxItems="localMaxItems"
                :maxFilesize="maxFilesize"
                :maxTotalFilesize="maxTotalFilesize"
                :disabled="readOnly"
                disabledPlaceholderText="No Files"
                @update="handleListUpdate"
                @all-uploaded="handleAllUploaded"
            ></FileSlingerListUploader>
        </div>
    `,
    data() {
        return {
            readOnly: false,  // TODO - make this dynamic based on colDef or something?
            initialFileObjects: [],  // if we are not doing v-model... only setting this with initial value, then reporting back up?
            localFileIds: [],  
            localFileObjects: [],
            localMaxItems: this.maxItems,  // override to 1 if single=true in created() 
            smallList: true,  // always for now
        }
    },
    computed: {
        parentComponent() {
            return this.params.context.componentParent;
        },
        liveGridCol() {
            return this.parentComponent.liveGridColsByField[this.params.colDef.field];
        },
        fileCategory() {
            return this.params.fileInputProps?.fileCategory || '';
        },
        parentId() {
            return this.params.fileInputProps?.parentId || '';
        },
        single() {
            return this.params.fileInputProps?.single || false;
        },
        maxItems() {
            return this.params.fileInputProps?.maxItems || 10;
        },
        imageOnly() {
            return this.params.fileInputProps?.imageOnly || false;
        },
        accept() {
            return this.params.fileInputProps?.accept || '';
        },
        maxFilesize() {  // max for ONE file in bytes - make sure it comes from / matches your backend
            return this.params.fileInputProps?.maxFilesize || (1024 * 1024 * 10);  // 10MB default - must make sure your s3file class can take it!
        },
        maxTotalFilesize() {  // TOTAL of all files - useful for things like email attachments when we have limit of all files combined
            // maybe allow strings here... parse them to size... must be npm package for it...
            return this.params.fileInputProps?.maxTotalFilesize || null;
        },
        hideLimits() {  // hide things like max files, max filesize, etc. shown under the drop zone
            return this.params.fileInputProps?.hideLimits || false;
        },
        // MOVE ALL THE FILE INPUT COMPONENT PROPS TO NESTED OBJECT
        // TODO - move them all to here... so we don't have to keep adding them one by one, and so we don't collide with other props we might add to params in future
        // fileInputProps() {
        //     return this.params.fileInputProps || {};
        // },
    },
    created() {
        // set the local things... but this will not be reactive unless you set a watcher...
        // we will filter out any falsey values in the array before setting on internal component
        // why? a temporary workaround for backend having stale data, still having reference to a file that was deleted, not finding it, and using None as its value in the list
        // root cause of that is front-end form submission, does not submit empty list when last file was deleted if field is required... so need to fix that first, then you could possibly remove this
        // but probably no harm to keep the falsey filter here... just might cover up other bugs, which is not ideal

        // must deal with 'single' prop - internal data is ALWAYS array though
        // we will just ignore single prop here and always convert to array internally... will work whether we get array or single object 
        // but we must override any maxItems to 1 if single = true

        if (this.single) {
            this.localMaxItems = 1;
        }

        const hasIncomingValue = !!this.params.value && Array.isArray(this.params.value) && this.params.value.length > 0;

        if (!hasIncomingValue) {
            this.initialFileObjects = [];  // no incoming?  just set internal initial value to empty array
        } else {
            // we have some value..
            const incomingFileArrayRaw = Array.isArray(this.params.value) ? [...this.params.value] : [this.params.value];
            // now filter that (in case you had nulls in the array... which can happen if something is goofy in backend)
            // then make a copy of all objects so we don't mutate the stuff we passed in
            this.initialFileObjects = incomingFileArrayRaw.filter((f) => !!f).map(fileObj => ({...fileObj}));
            this.handleListUpdate(this.initialFileObjects);  // set the localFileObjects and localFileIds
        }
    },
    mounted() {
        this.$nextTick(() => {
            // need to do this stuff to make all the tabbing/keydown stuff nice
            const focusRef = this.$refs.fileEditorOuter;  
            if (focusRef && focusRef.focus) {
                console.log('focusing file uploader', focusRef);
                focusRef.focus();  // might have to get its element?
            }
        });
    },
    methods: {
        getValue() {
            // the grid calls this to get the value from the editor when it is done
            // can it be async????  what if file is not done uploading yet???
            // now set value in form
            // must change this based on whether we have this.single === true
            let valueToReturn = null;
            if (this.single) {
                valueToReturn = this.localFileObjects.length ? this.localFileObjects[0] : null;
            } else {
                valueToReturn = this.localFileObjects;
            }
            return valueToReturn;
        },
        // Gets called once before editing starts, to give editor a chance to
        // cancel the editing before it even starts.
        isCancelBeforeStart() {
            return false;
        },
        isCancelAfterEnd() {
            // Gets called once when editing is finished (eg if Enter is pressed).
            // If you return true, then the result of the edit will be ignored.
            return false;
            // return this.localAmountIsValid ? false : true;  // if valid, return false (meaning don't cancel), if invalid, return true (meaning cancel the edit)
        },
        isPopup() {
            return true;
        },
        getPopupPosition() {
            return 'under';  // can be 'over' or 'under'  (over the cell, or under the bottom of cell)
        },
        // focusIn() {},
        // focusOut() {},
        // focusAmountInput() {
            //     this.$refs.amountInput.focus();
            // },
            // focusCurrencyInput() {
                //     this.$refs.currencyInput.focus();
                // },
        // handle inputs
        handleListUpdate(newList) {
            // newList will be array of objects with our file data (standard format to/from backend for s3 files)
            console.log('new file list', newList);
            this.localFileObjects = newList.map(f => ({...f}));  // shallow copy, always an array
            this.localFileIds = newList.map(f => f.id);
            // then grid will call getValue() when it wants the value
            // call stop editing to tell grid we are done
            // NO!  cannot do it only on the update event... that gets called before they are done... so later ones get "lost"
            // need to add an event in child component for when all pending uploads are DONE 
            // this.params.stopEditing();  // takes optional arg to suppressNavigateAfterEdit (boolean)
            // probably should also call auto size on the column?
        },
        handleAllUploaded() {
            // called when all files are uploaded (no pending uploads left)
            // now we can tell the grid we are done editing
            this.params.stopEditing();  // takes optional arg to suppressNavigateAfterEdit (boolean)
            // probably should also call auto size on the column?... that can make it super wide, not really expected... but adjusting height would be nice.
            setTimeout(() => {
                this.params.api.resetRowHeights();  // will call getRowHeight for each row
                // this.params.columnApi.autoSizeColumn(this.params.column.getId());
            }, 100);  // slight delay to let grid settle down first
        },
    },
});

export {
    AgGridFileRenderer,
    AgGridFileEditor,
}